import React, { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Search, CheckCircle2, ClipboardList, Layers, Calculator } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { api, errMsg, fmtINR } from "../lib/api";
import { MissingRatesBanner } from "./MissingRatesBanner";
import { ReadOnlyNotice } from "./FeatureGate";

const EMPTY_PROP = {
  pin: "", owner_name: "", father_spouse_name: "", mobile: "", address: "",
  zone_name: "", ward_name: "", mohalla_name: "", road_width_id: "",
};
const EMPTY_LINE = { tax_head_id: "", usage_label: "", house_no: "", arrear_amount: "", current_amount: "", financial_year_id: "" };
const EMPTY_FLOOR = {
  floor_label: "", house_no: "", property_type_id: "", const_type_id: "", usage_type_id: "", sub_usage_id: "", fee_category_id: "", fee_sub_category_id: "",
  plot_area_sqft: "", open_area_sqft: "", constructed_area_sqft: "", carpet_area_sqft: "",
  electricity_ca_no: "", construction_date: "", construction_age: "",
};
const MASTER_KEYS = ["zones", "wards", "mohallas", "categories", "sub-categories", "property-types", "construction-types", "tax-heads", "usage-types", "sub-usage-types", "floors", "road-widths"];

export const ManualDemandEntry = ({ code, readOnly = false }) => {
  const [fys, setFys] = useState([]);
  const [fyId, setFyId] = useState("");
  const [calcFyIds, setCalcFyIds] = useState([]);   // multi-FY demand capture
  const [form, setForm] = useState({ ...EMPTY_PROP });
  const [floors, setFloors] = useState([{ ...EMPTY_FLOOR }]);
  const [lines, setLines] = useState([{ ...EMPTY_LINE }]);
  const [masters, setMasters] = useState(Object.fromEntries(MASTER_KEYS.map((k) => [k, []])));
  const [calc, setCalc] = useState(null);
  const [calcBusy, setCalcBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [looking, setLooking] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`/t/${code}/masters/financial-years`).then((r) => {
      setFys(r.data);
      const cur = r.data.find((f) => f.is_current);
      if (cur) {
        setFyId(cur.id);
        setCalcFyIds((v) => (v.length ? v : [cur.id]));
      }
    }).catch(() => {});
  }, [code]);

  const fyLabel = (id) => {
    const f = (fys || []).find((x) => x.id === id);
    return f ? `${f.fy_label}${f.is_current ? " (current)" : ""}` : null;
  };

  const loadMasters = useCallback(async () => {
    const pairs = await Promise.all(MASTER_KEYS.map((k) =>
      api.get(`/t/${code}/masters/${k}`).then((r) => [k, r.data]).catch(() => [k, []])));
    setMasters(Object.fromEntries(pairs));
  }, [code]);
  useEffect(() => { loadMasters(); }, [loadMasters]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLine = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const setFloor = (i, k, v) => setFloors((fs) => fs.map((f, j) => {
    if (j !== i) return f;
    const next = { ...f, [k]: v };
    if (k === "usage_type_id") next.sub_usage_id = "";        // cascade reset
    if (k === "fee_category_id") next.fee_sub_category_id = ""; // cascade reset
    if (k === "plot_area_sqft" || k === "open_area_sqft") {   // constructed auto = plot - open
      const plot = Number(k === "plot_area_sqft" ? v : next.plot_area_sqft) || 0;
      const open = Number(k === "open_area_sqft" ? v : next.open_area_sqft) || 0;
      if (plot > 0) {
        const constructed = Math.max(0, Math.round((plot - open) * 100) / 100);
        next.constructed_area_sqft = String(constructed);
        next.carpet_area_sqft = String(Math.round(constructed * 0.8 * 100) / 100);
      }
    }
    if (k === "constructed_area_sqft") {                       // carpet auto = 80% of constructed
      next.carpet_area_sqft = v && !isNaN(Number(v)) ? String(Math.round(Number(v) * 0.8 * 100) / 100) : f.carpet_area_sqft;
    }
    return next;
  }));

  // ---- master name helpers ----
  const norm = (s) => (s || "").trim().toLowerCase();
  const uName = (id) => (masters["usage-types"] || []).find((u) => u.id === id)?.usage_type_name || "";
  const suName = (id) => (masters["sub-usage-types"] || []).find((s) => s.id === id)?.sub_usage_type_name || "";
  const cName = (id) => (masters.categories || []).find((c) => c.id === id)?.category_name || "";
  const scName = (id) => (masters["sub-categories"] || []).find((s) => s.id === id)?.sub_category_name || "";
  const ptName = (id) => (masters["property-types"] || []).find((p) => p.id === id)?.type_name || "";
  const ctName = (id) => (masters["construction-types"] || []).find((c) => c.id === id)?.const_type_name || "";
  const rwName = (id) => (masters["road-widths"] || []).find((r) => r.id === id)?.width_label || "";

  // ---- cascading location selects: Zone -> Ward -> Mohalla ----
  const selZone = (masters.zones || []).find((z) => norm(z.zone_name) === norm(form.zone_name));
  const wardOptions = selZone ? (masters.wards || []).filter((w) => w.zone_id === selZone.id) : masters.wards || [];
  const selWard = wardOptions.find((w) => norm(w.ward_name) === norm(form.ward_name)) ||
    (masters.wards || []).find((w) => norm(w.ward_name) === norm(form.ward_name));
  const mohallaOptions = selWard ? (masters.mohallas || []).filter((m) => m.ward_id === selWard.id) : masters.mohallas || [];
  const subUsageOf = (usageId) => (masters["sub-usage-types"] || []).filter((s) => s.usage_type_id === usageId);
  const subCatOf = (catId) => (masters["sub-categories"] || []).filter((s) => s.category_id === catId);

  // ---- floor-wise area totals ----
  const sumF = (k) => Math.round(floors.reduce((s, f) => s + (Number(f[k]) || 0), 0) * 100) / 100;
  const totals = {
    plot: sumF("plot_area_sqft"), open: sumF("open_area_sqft"),
    constructed: sumF("constructed_area_sqft"), carpet: sumF("carpet_area_sqft"),
  };
  const summaryFloors = floors.filter((f) => f.usage_type_id || f.property_type_id || f.const_type_id || Number(f.constructed_area_sqft) > 0 || Number(f.plot_area_sqft) > 0);

  const taxHeadByCode = (codeStr) => {
    const heads = masters["tax-heads"] || [];
    return heads.find((h) => (h.tax_code || "").toUpperCase() === codeStr)?.id ||
      heads.find((h) => norm(h.tax_head_name).includes(codeStr === "PT" ? "property" : "user"))?.id || "";
  };

  const lookupPin = async () => {
    if (!form.pin.trim()) return toast.error("Enter a PIN to look up");
    setLooking(true);
    try {
      const r = await api.get(`/t/${code}/citizen/search`, { params: { pin: form.pin.trim() } });
      const p = r.data.property;
      setForm((f) => ({
        ...f, pin: p.pin, owner_name: p.owner_name || "", father_spouse_name: p.father_spouse_name || "",
        mobile: p.mobile || "", address: p.address || "",
        zone_name: p.zone_label || f.zone_name, ward_name: p.ward_label || f.ward_name,
        mohalla_name: p.mohalla_label || f.mohalla_name,
        road_width_id: p.road_width_id || f.road_width_id,
      }));
      if (Array.isArray(p.assessment_floors) && p.assessment_floors.length > 0) {
        setFloors(p.assessment_floors.map((af) => ({
          floor_label: af.floor_label || "", house_no: af.house_no || p.house_no || "",
          property_type_id: af.property_type_id || p.property_type_id || "",
          const_type_id: af.const_type_id || p.const_type_id || "",
          usage_type_id: af.usage_type_id || "", sub_usage_id: af.sub_usage_id || "",
          fee_category_id: af.fee_category_id || "", fee_sub_category_id: af.fee_sub_category_id || "",
          plot_area_sqft: af.plot_area_sqft != null ? String(af.plot_area_sqft) : "",
          open_area_sqft: af.open_area_sqft != null ? String(af.open_area_sqft) : "",
          constructed_area_sqft: af.constructed_area_sqft != null ? String(af.constructed_area_sqft) : (af.area_sqft != null ? String(af.area_sqft) : ""),
          carpet_area_sqft: af.carpet_area_sqft != null ? String(af.carpet_area_sqft) : "",
        })));
      } else {
        // no saved floors yet: seed the first floor from the property-level data
        setFloors([{
          ...EMPTY_FLOOR,
          house_no: p.house_no || "",
          property_type_id: p.property_type_id || "",
          const_type_id: p.const_type_id || "",
          fee_category_id: p.category_id || "",
          fee_sub_category_id: p.sub_category_id || "",
          plot_area_sqft: p.plot_area_sqft != null ? String(p.plot_area_sqft) : "",
          open_area_sqft: p.open_area_sqft != null ? String(p.open_area_sqft) : "",
          constructed_area_sqft: p.constructed_area_sqft != null ? String(p.constructed_area_sqft) : "",
          carpet_area_sqft: p.carpet_area_sqft != null ? String(p.carpet_area_sqft) : "",
        }]);
      }
      toast.success(`Existing property ${p.pin} loaded — new demands will be added to it`);
    } catch (err) {
      toast.info("No existing property with this PIN — a new property will be created");
    } finally {
      setLooking(false);
    }
  };

  const totalOf = (l) => (Number(l.arrear_amount) || 0) + (Number(l.current_amount) || 0);
  const grandTotal = lines.reduce((s, l) => s + totalOf(l), 0);
  const houseNumbers = (fls) => [...new Set(fls.map((f) => (f.house_no || "").trim()).filter(Boolean))].join(", ");
  const houseOfUsage = (label) => {
    const matched = summaryFloors.filter((f) => {
      const un = uName(f.usage_type_id);
      return un && (label || "").toLowerCase().includes(un.toLowerCase());
    });
    return houseNumbers(matched.length > 0 ? matched : summaryFloors);
  };

  // ---- calculate via Rate Engine (ARV matrix method, per selected FY) ----
  const runCalculate = async () => {
    const targetFys = calcFyIds.length ? calcFyIds : (fyId ? [fyId] : []);
    if (targetFys.length === 0) return toast.error("Select at least one financial year");
    if (!form.road_width_id) return toast.error("Select a road width first — it picks the ARV rate column");
    const valid = floors.filter((f) => f.usage_type_id && (Number(f.constructed_area_sqft) > 0 || Number(f.carpet_area_sqft) > 0));
    if (valid.length === 0 && !(totals.open > 0)) return toast.error("Add at least one floor with usage type and area (or an open/vacant area)");
    setCalcBusy(true);
    try {
      const mkBody = (fid) => ({
        financial_year_id: fid,
        road_width_id: form.road_width_id,
        plot_area_sqft: totals.plot || null,
        vacant_area_sqft: totals.open || null,
        floors: valid.map((f) => ({
          floor_label: f.floor_label || "Floor", usage_type_id: f.usage_type_id,
          sub_usage_id: f.sub_usage_id || null,
          const_type_id: f.const_type_id || null,
          area_sqft: Number(f.constructed_area_sqft) || 0,
          carpet_area_sqft: Number(f.carpet_area_sqft) || null,
          fee_category_id: f.fee_category_id || null, fee_sub_category_id: f.fee_sub_category_id || null,
          owner_name: form.owner_name || null,
        })),
      });
      const results = [];
      for (const fid of targetFys) {
        const r = await api.post(`/t/${code}/rate-engine/calculate`, mkBody(fid));
        results.push(r.data);
      }
      const first = results[0];
      const combined = results.length === 1 ? first : {
        ...first,
        multi_year: true,
        per_year: results.map((r) => ({ financial_year_id: r.financial_year_id, fy_label: r.fy_label,
          arv_total_yearly: r.arv_total_yearly, total_yearly: r.grand_total?.total_yearly || 0 })),
        proposed_demands: results.flatMap((r) => r.proposed_demands || []),
        grand_total: { total_yearly: Math.round(results.reduce((s, r) => s + (r.grand_total?.total_yearly || 0), 0) * 100) / 100 },
        warnings: [...new Set(results.flatMap((r) => r.warnings || []))],
      };
      setCalc(combined);
      const newLines = (combined.proposed_demands || []).map((p) => ({
        tax_head_id: p.tax_head_id || taxHeadByCode(p.tax_code),
        usage_label: p.label || p.usage_label || "",
        house_no: p.tax_code === "UC" ? houseOfUsage(p.usage_label) : houseNumbers(summaryFloors),
        arrear_amount: "",
        current_amount: String(p.current_amount),
        financial_year_id: p.financial_year_id || fyId,
      })).filter((l) => l.tax_head_id);
      if (newLines.length > 0) {
        setLines(newLines);
        toast.success(`Calculated for ${targetFys.length} year(s) — ${newLines.length} demand line(s) filled`);
      } else {
        toast.warning("Calculated, but no demand lines were produced — check the ARV matrix and tax percentages in Rate Engine");
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setCalcBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (readOnly) return toast.error("View only — you do not have permission to save demands");
    if (!fyId) return toast.error("Select a financial year");
    if (!form.road_width_id) return toast.error("Road width is required — select one in Property details");
    const validLines = lines.filter((l) => l.tax_head_id && totalOf(l) > 0);
    if (validLines.length === 0) return toast.error("Add at least one demand line with a tax head and amount");
    setBusy(true);
    const f0 = floors[0] || {};
    try {
      const r = await api.post(`/t/${code}/demands/manual`, {
        financial_year_id: fyId,
        ...form,
        house_no: houseNumbers(summaryFloors) || null,
        property_type_id: f0.property_type_id || null,
        const_type_id: f0.const_type_id || null,
        category_name: cName(f0.fee_category_id) || null,
        sub_category_name: scName(f0.fee_sub_category_id) || null,
        plot_area_sqft: totals.plot || null,
        open_area_sqft: totals.open || null,
        constructed_area_sqft: totals.constructed || null,
        carpet_area_sqft: totals.carpet || null,
        assessment_floors: summaryFloors.map((f) => ({
          floor_label: f.floor_label || "Floor", house_no: (f.house_no || "").trim() || null,
          property_type_id: f.property_type_id || null, property_type_name: ptName(f.property_type_id) || null,
          const_type_id: f.const_type_id || null, const_type_name: ctName(f.const_type_id) || null,
          usage_type_id: f.usage_type_id || null, usage_type_name: uName(f.usage_type_id) || null,
          sub_usage_id: f.sub_usage_id || null, sub_usage_name: suName(f.sub_usage_id) || null,
          fee_category_id: f.fee_category_id || null, fee_category_name: cName(f.fee_category_id) || null,
          fee_sub_category_id: f.fee_sub_category_id || null, fee_sub_category_name: scName(f.fee_sub_category_id) || null,
          plot_area_sqft: Number(f.plot_area_sqft) || 0, open_area_sqft: Number(f.open_area_sqft) || 0,
          constructed_area_sqft: Number(f.constructed_area_sqft) || 0, carpet_area_sqft: Number(f.carpet_area_sqft) || 0,
          electricity_ca_no: (f.electricity_ca_no || "").trim() || null,
          construction_date: f.construction_date || null,
          construction_age: f.construction_age === "" || f.construction_age == null ? null : Number(f.construction_age),
        })),
        demands: validLines.map((l) => ({
          tax_head_id: l.tax_head_id, usage_label: l.usage_label || null,
          house_no: (l.house_no || "").trim() || null,
          arrear_amount: Number(l.arrear_amount) || 0, current_amount: Number(l.current_amount) || 0,
          financial_year_id: l.financial_year_id || fyId,
        })),
      });
      setResult(r.data);
      toast.success(`Saved — PIN ${r.data.pin}: ${r.data.demands_created} demand(s) created, ${r.data.demands_updated} updated`);
      setForm({ ...EMPTY_PROP });
      setFloors([{ ...EMPTY_FLOOR }]);
      setLines([{ ...EMPTY_LINE }]);
      setCalc(null);
      loadMasters();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-4" aria-readonly={readOnly || undefined}>
      {readOnly && (
        <div className="lg:col-span-3">
          <ReadOnlyNotice what="the manual entry form" testId="manual-entry-readonly-notice" />
        </div>
      )}
      <fieldset disabled={readOnly} className="contents" data-testid="manual-entry-fieldset">
      <div className="lg:col-span-3">
        <MissingRatesBanner code={code} fyId={fyId} />
      </div>
      <div className="lg:col-span-2 space-y-4">
        <Card className="shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-base font-heading">Property details</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>PIN</Label>
                <div className="flex gap-1.5">
                  <Input value={form.pin} onChange={(e) => set("pin", e.target.value)} placeholder="Blank = auto"
                    data-testid="manual-pin-input" />
                  <Button type="button" variant="outline" size="icon" onClick={lookupPin} disabled={looking}
                    title="Look up existing property" data-testid="manual-pin-lookup-button">
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label>Owner name *</Label>
                <Input value={form.owner_name} onChange={(e) => set("owner_name", e.target.value)} required
                  data-testid="manual-owner-input" />
              </div>
              <div className="space-y-1">
                <Label>Father / Spouse</Label>
                <Input value={form.father_spouse_name} onChange={(e) => set("father_spouse_name", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Mobile</Label>
                <Input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} />
              </div>
              <div className="space-y-1 sm:col-span-3">
                <Label>Address of property / owner</Label>
                <Input value={form.address} onChange={(e) => set("address", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Zone</Label>
                <Select value={form.zone_name || ""} onValueChange={(v) => v && setForm((f) => ({ ...f, zone_name: v, ward_name: "", mohalla_name: "" }))}>
                  <SelectTrigger data-testid="manual-zone-select"><SelectValue placeholder="Select zone">{form.zone_name || null}</SelectValue></SelectTrigger>
                  <SelectContent>
                    {(masters.zones || []).map((z) => <SelectItem key={z.id} value={z.zone_name}>{z.zone_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Ward</Label>
                <Select value={form.ward_name || ""} onValueChange={(v) => v && setForm((f) => ({ ...f, ward_name: v, mohalla_name: "" }))}
                  disabled={wardOptions.length === 0}>
                  <SelectTrigger data-testid="manual-ward-select">
                    <SelectValue placeholder={selZone ? "Select ward" : "Pick zone first (or all wards)"}>{form.ward_name || null}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {wardOptions.map((w) => <SelectItem key={w.id} value={w.ward_name}>{w.ward_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Mohalla</Label>
                <Select value={form.mohalla_name || ""} onValueChange={(v) => v && set("mohalla_name", v)}
                  disabled={mohallaOptions.length === 0}>
                  <SelectTrigger data-testid="manual-mohalla-select">
                    <SelectValue placeholder={!selWard ? "Pick ward first" : (mohallaOptions.length === 0 ? "No mohallas in this ward" : "Select mohalla")}>{form.mohalla_name || null}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {mohallaOptions.map((m) => <SelectItem key={m.id} value={m.mohalla_name}>{m.mohalla_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Road width *</Label>
                <Select value={form.road_width_id || ""} onValueChange={(v) => v && set("road_width_id", v)}>
                  <SelectTrigger data-testid="manual-roadwidth-select">
                    <SelectValue placeholder="Select road width">{rwName(form.road_width_id) || null}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {(masters["road-widths"] || []).map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.width_label}{r.width_in_feet != null && String(r.width_in_feet) !== String(r.width_label) ? ` (${r.width_in_feet})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Layers className="h-4 w-4" /> Floor-wise assessment
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Enter areas per floor. ARV = carpet area × matrix rate (construction × road width × C/R/M) × 12 • Tax lines = ARV × annual % • User Charges = category rate × 12 • Constructed auto = Plot − Open • Carpet auto = 80% of constructed
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {floors.map((f, i) => (
              <div key={i} className="border rounded-lg p-3 space-y-2" data-testid={`assessment-floor-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 flex-1">
                    <Select value={f.floor_label || ""} onValueChange={(v) => v && setFloor(i, "floor_label", v)}>
                      <SelectTrigger data-testid={`floor-select-${i}`}><SelectValue placeholder="Floor">{f.floor_label || null}</SelectValue></SelectTrigger>
                      <SelectContent>
                        {(masters.floors || []).map((fl) => <SelectItem key={fl.id} value={fl.floor_label}>{fl.floor_label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input placeholder="House No." value={f.house_no}
                      onChange={(e) => setFloor(i, "house_no", e.target.value)} data-testid={`floor-houseno-input-${i}`} />
                    <Select value={f.property_type_id || ""} onValueChange={(v) => v && setFloor(i, "property_type_id", v)}>
                      <SelectTrigger data-testid={`floor-ptype-select-${i}`}><SelectValue placeholder="Property type">{ptName(f.property_type_id) || null}</SelectValue></SelectTrigger>
                      <SelectContent>
                        {(masters["property-types"] || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.type_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={f.const_type_id || ""} onValueChange={(v) => v && setFloor(i, "const_type_id", v)}>
                      <SelectTrigger data-testid={`floor-ctype-select-${i}`}><SelectValue placeholder="Construction type">{ctName(f.const_type_id) || null}</SelectValue></SelectTrigger>
                      <SelectContent>
                        {(masters["construction-types"] || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.const_type_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {floors.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" className="h-9 w-9"
                      onClick={() => setFloors(floors.filter((_, j) => j !== i))} data-testid={`floor-remove-${i}`}>
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Select value={f.usage_type_id || ""} onValueChange={(v) => v && setFloor(i, "usage_type_id", v)}>
                    <SelectTrigger data-testid={`floor-usage-select-${i}`}><SelectValue placeholder="Usage type * (Property Tax)">{uName(f.usage_type_id) || null}</SelectValue></SelectTrigger>
                    <SelectContent>
                      {(masters["usage-types"] || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.usage_type_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={f.sub_usage_id || ""} onValueChange={(v) => v && setFloor(i, "sub_usage_id", v)}
                    disabled={!f.usage_type_id || subUsageOf(f.usage_type_id).length === 0}>
                    <SelectTrigger data-testid={`floor-subusage-select-${i}`}>
                      <SelectValue placeholder={!f.usage_type_id ? "Pick usage first" : (subUsageOf(f.usage_type_id).length === 0 ? "No sub-usages" : "Sub-usage")}>{suName(f.sub_usage_id) || null}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {subUsageOf(f.usage_type_id).map((s) => <SelectItem key={s.id} value={s.id}>{s.sub_usage_type_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={f.fee_category_id || ""} onValueChange={(v) => v && setFloor(i, "fee_category_id", v)}>
                    <SelectTrigger data-testid={`floor-category-select-${i}`}><SelectValue placeholder="Category (User Charges)">{cName(f.fee_category_id) || null}</SelectValue></SelectTrigger>
                    <SelectContent>
                      {(masters.categories || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.category_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={f.fee_sub_category_id || ""} onValueChange={(v) => v && setFloor(i, "fee_sub_category_id", v)}
                    disabled={!f.fee_category_id || subCatOf(f.fee_category_id).length === 0}>
                    <SelectTrigger data-testid={`floor-subcategory-select-${i}`}>
                      <SelectValue placeholder={!f.fee_category_id ? "Pick category first" : (subCatOf(f.fee_category_id).length === 0 ? "No sub-categories" : "Sub-category")}>{scName(f.fee_sub_category_id) || null}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {subCatOf(f.fee_category_id).map((s) => <SelectItem key={s.id} value={s.id}>{s.sub_category_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="space-y-0.5">
                    <Label className="text-[11px]">Plot Area (sqft)</Label>
                    <Input type="number" step="any" min="0" value={f.plot_area_sqft}
                      onChange={(e) => setFloor(i, "plot_area_sqft", e.target.value)} data-testid={`floor-plot-input-${i}`} />
                  </div>
                  <div className="space-y-0.5">
                    <Label className="text-[11px]">Open Area (sqft)</Label>
                    <Input type="number" step="any" min="0" value={f.open_area_sqft}
                      onChange={(e) => setFloor(i, "open_area_sqft", e.target.value)} data-testid={`floor-open-input-${i}`} />
                  </div>
                  <div className="space-y-0.5">
                    <Label className="text-[11px]">Constructed Area (sqft) *</Label>
                    <Input type="number" step="any" min="0" value={f.constructed_area_sqft}
                      onChange={(e) => setFloor(i, "constructed_area_sqft", e.target.value)} data-testid={`floor-constructed-input-${i}`} />
                  </div>
                  <div className="space-y-0.5">
                    <Label className="text-[11px]">Carpet Area (sqft)</Label>
                    <Input type="number" step="any" min="0" value={f.carpet_area_sqft}
                      onChange={(e) => setFloor(i, "carpet_area_sqft", e.target.value)} data-testid={`floor-carpet-input-${i}`} />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="space-y-0.5">
                    <Label className="text-[11px]">Electricity CA No</Label>
                    <Input value={f.electricity_ca_no} placeholder="CA number"
                      onChange={(e) => setFloor(i, "electricity_ca_no", e.target.value)} data-testid={`floor-electricity-input-${i}`} />
                  </div>
                  <div className="space-y-0.5">
                    <Label className="text-[11px]">Construction Date</Label>
                    <Input type="date" value={f.construction_date}
                      onChange={(e) => {
                        const d = e.target.value;
                        setFloors((fs) => fs.map((fl, j) => {
                          if (j !== i) return fl;
                          const age = d ? new Date().getFullYear() - new Date(d).getFullYear() : fl.construction_age;
                          return { ...fl, construction_date: d, construction_age: d && age >= 0 ? age : fl.construction_age };
                        }));
                      }} data-testid={`floor-constdate-input-${i}`} />
                  </div>
                  <div className="space-y-0.5">
                    <Label className="text-[11px]">Construction Age (yrs, auto)</Label>
                    <Input type="number" min="0" value={f.construction_age} placeholder="Years"
                      onChange={(e) => setFloor(i, "construction_age", e.target.value === "" ? "" : Number(e.target.value))}
                      data-testid={`floor-constage-input-${i}`} />
                  </div>
                </div>
              </div>
            ))}
            <div className="border rounded-lg px-3 py-2 space-y-1" data-testid="manual-calc-fy-box">
              <p className="text-xs font-medium">Calculate & generate demand for year(s)</p>
              <p className="text-[11px] text-muted-foreground">Pick current and/or previous years — each year uses its own rates and gets its own demand.</p>
              <div className="flex flex-wrap gap-2 pt-1">
                {(fys || []).map((f) => (
                  <label key={f.id} className={`flex items-center gap-1.5 border rounded-md px-2 py-1 text-xs cursor-pointer ${calcFyIds.includes(f.id) ? "border-primary bg-accent" : ""}`}>
                    <input type="checkbox" className="accent-current h-3.5 w-3.5" checked={calcFyIds.includes(f.id)}
                      onChange={() => setCalcFyIds((v) => (v.includes(f.id) ? v.filter((x) => x !== f.id) : [...v, f.id]))}
                      data-testid={`manual-calcfy-check-${f.fy_label}`} />
                    {f.fy_label}{f.is_current ? " (current)" : ""}
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button type="button" variant="outline" size="sm" onClick={() => setFloors([...floors, { ...EMPTY_FLOOR }])}
                data-testid="add-floor-button">
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Add floor
              </Button>
              <Button type="button" size="sm" onClick={runCalculate} disabled={calcBusy} data-testid="calculate-button">
                <Calculator className="h-3.5 w-3.5 mr-1.5" /> {calcBusy ? "Calculating..." : "Calculate Tax & User Charges"}
              </Button>
            </div>

            {summaryFloors.length > 0 && (
              <div className="border rounded-lg overflow-x-auto" data-testid="floor-summary-sheet">
                <p className="text-xs font-semibold px-3 pt-2.5 pb-1">Summary sheet</p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Floor</TableHead>
                      <TableHead className="text-xs">House No</TableHead>
                      <TableHead className="text-xs">Property type</TableHead>
                      <TableHead className="text-xs">Construction</TableHead>
                      <TableHead className="text-xs">Usage / Sub-usage</TableHead>
                      <TableHead className="text-xs text-right">Plot</TableHead>
                      <TableHead className="text-xs text-right">Open</TableHead>
                      <TableHead className="text-xs text-right">Constructed</TableHead>
                      <TableHead className="text-xs text-right">Carpet</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {summaryFloors.map((f, i) => (
                      <TableRow key={i} data-testid={`summary-row-${i}`}>
                        <TableCell className="text-xs font-medium">{f.floor_label || `Floor ${i + 1}`}</TableCell>
                        <TableCell className="text-xs">{f.house_no || "—"}</TableCell>
                        <TableCell className="text-xs">{ptName(f.property_type_id) || "—"}</TableCell>
                        <TableCell className="text-xs">{ctName(f.const_type_id) || "—"}</TableCell>
                        <TableCell className="text-xs">
                          {uName(f.usage_type_id) || "—"}
                          {suName(f.sub_usage_id) ? <span className="text-muted-foreground"> / {suName(f.sub_usage_id)}</span> : ""}
                        </TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{Number(f.plot_area_sqft) || 0}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{Number(f.open_area_sqft) || 0}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{Number(f.constructed_area_sqft) || 0}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{Number(f.carpet_area_sqft) || 0}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted/60 font-semibold" data-testid="summary-total-row">
                      <TableCell className="text-xs" colSpan={5}>Total ({summaryFloors.length} floor{summaryFloors.length > 1 ? "s" : ""})</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{totals.plot}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{totals.open}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums" data-testid="summary-total-constructed">{totals.constructed}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums" data-testid="summary-total-carpet">{totals.carpet}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}

            {calc && (
              <div className="border rounded-lg p-3 bg-muted/40 space-y-2" data-testid="calc-result">
                {calc.road_width_label && (
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Road width (rate column)</span><span data-testid="calc-band-label">{calc.road_width_label}</span>
                  </div>
                )}
                {(calc.groups || []).map((g, i) => (
                  <div key={i} className="flex items-center justify-between text-sm gap-2 flex-wrap">
                    <span className="truncate">{g.usage_label} <span className="text-muted-foreground">({g.carpet_area_sqft || g.area_sqft} sqft carpet · {g.arv_class})</span></span>
                    <span className="font-mono text-xs">ARV {fmtINR(g.arv_yearly)}/yr • UC {fmtINR(g.user_fee_yearly)}/yr</span>
                  </div>
                ))}
                {calc.vacant_arv_yearly > 0 && (
                  <div className="flex items-center justify-between text-sm gap-2">
                    <span>Vacant land <span className="text-muted-foreground">({calc.vacant_area_sqft} sqft)</span></span>
                    <span className="font-mono text-xs">ARV {fmtINR(calc.vacant_arv_yearly)}/yr</span>
                  </div>
                )}
                {calc.multi_year && (calc.per_year || []).map((y, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">FY {y.fy_label} — ARV {fmtINR(y.arv_total_yearly)}</span>
                    <span className="font-mono text-xs">{fmtINR(y.total_yearly)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between text-sm border-t pt-2">
                  <span className="text-muted-foreground">{calc.multi_year ? "ARV (current selection)" : "Total ARV (annual value)"}</span>
                  <span className="font-mono" data-testid="calc-arv-total">{fmtINR(calc.arv_total_yearly || 0)}</span>
                </div>
                {(calc.proposed_demands || []).map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{calc.multi_year && p.fy_label ? `${p.fy_label} • ` : ""}{p.label}{p.percent ? ` (${p.percent}% of ARV)` : ""}</span>
                    <span className="tabular-nums">{fmtINR(p.current_amount)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between border-t pt-2 text-sm font-semibold">
                  <span>Total annual demand</span>
                  <span data-testid="calc-grand-total">{fmtINR(calc.grand_total?.total_yearly || 0)}</span>
                </div>
                {(calc.warnings || []).length > 0 && (
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 space-y-0.5">
                    {calc.warnings.map((w, i) => <p key={i}>{w}</p>)}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="space-y-4">
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2"><ClipboardList className="h-4 w-4" /> Demand lines</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1">
              <Label>Financial year</Label>
              <Select value={fyId} onValueChange={(v) => v && setFyId(v)}>
                <SelectTrigger data-testid="manual-fy-select"><SelectValue placeholder="Select FY">{fyLabel(fyId)}</SelectValue></SelectTrigger>
                <SelectContent>
                  {(fys || []).map((f) => <SelectItem key={f.id} value={f.id}>{f.fy_label}{f.is_current ? " (current)" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {lines.map((l, i) => (
              <div key={i} className="border rounded-lg p-2.5 space-y-2" data-testid={`manual-demand-line-${i}`}>
                <div className="flex gap-2">
                  <Select value={l.tax_head_id} onValueChange={(v) => v && setLine(i, "tax_head_id", v)}>
                    <SelectTrigger className="flex-1" data-testid={`manual-taxhead-select-${i}`}><SelectValue placeholder="Tax head *" /></SelectTrigger>
                    <SelectContent>
                      {masters["tax-heads"].map((h) => <SelectItem key={h.id} value={h.id}>{h.tax_head_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {lines.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0"
                      onClick={() => setLines(lines.filter((_, j) => j !== i))}>
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  )}
                </div>
                <Input placeholder="Usage label (optional, e.g. Commercial - Ramesh)" value={l.usage_label}
                  onChange={(e) => setLine(i, "usage_label", e.target.value)} />
                <Select value={l.financial_year_id || fyId} onValueChange={(v) => v && setLine(i, "financial_year_id", v)}>
                  <SelectTrigger className="h-9" data-testid={`manual-line-fy-select-${i}`}>
                    <SelectValue placeholder="Financial year">{fyLabel(l.financial_year_id || fyId)}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {(fys || []).map((f) => <SelectItem key={f.id} value={f.id}>{f.fy_label}{f.is_current ? " (current)" : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input placeholder="House No. (from floor details)" value={l.house_no}
                  onChange={(e) => setLine(i, "house_no", e.target.value)} data-testid={`manual-line-houseno-${i}`} />
                <div className="grid grid-cols-2 gap-2">
                  <Input type="number" step="any" min="0" placeholder="Arrear ₹" value={l.arrear_amount}
                    onChange={(e) => setLine(i, "arrear_amount", e.target.value)} data-testid={`manual-arrear-input-${i}`} />
                  <Input type="number" step="any" min="0" placeholder="Current ₹" value={l.current_amount}
                    onChange={(e) => setLine(i, "current_amount", e.target.value)} data-testid={`manual-current-input-${i}`} />
                </div>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" className="w-full"
              onClick={() => setLines([...lines, { ...EMPTY_LINE }])} data-testid="manual-add-line-button">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Add demand line
            </Button>
            <div className="flex items-center justify-between border-t pt-3">
              <span className="text-sm text-muted-foreground">Total demand</span>
              <span className="font-semibold tabular-nums" data-testid="manual-total-demand">{fmtINR(grandTotal)}</span>
            </div>
            <Button type="submit" className="w-full" disabled={busy || readOnly} data-testid="manual-demand-submit-button">
              {readOnly ? "View only — saving disabled" : busy ? "Saving..." : "Save property & demands"}
            </Button>
          </CardContent>
        </Card>

        {result && (
          <Card className="shadow-sm border-emerald-200 bg-emerald-50/50" data-testid="manual-entry-result">
            <CardContent className="p-4 text-sm space-y-1">
              <p className="flex items-center gap-2 font-medium text-emerald-800">
                <CheckCircle2 className="h-4 w-4" /> Saved successfully
              </p>
              <p>PIN: <span className="font-mono font-medium">{result.pin}</span> ({result.property_created ? "new property" : "existing property updated"})</p>
              <p>{result.demands_created} demand(s) created, {result.demands_updated} updated
                {result.masters_auto_created > 0 ? `, ${result.masters_auto_created} master(s) auto-created` : ""}</p>
            </CardContent>
          </Card>
        )}
      </div>
      </fieldset>
    </form>
  );
};
